import {compact} from 'lodash';
import React, {FC, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {EventHandler} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {getColorHighlight} from '../../../../../helpers/models/highlightHelpers';
import {ConversationAiMarkModel} from '../../../../../models/api/conversationAiMarkModel';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {PermissionsEnum} from '../../../../../models/api/roleModel';
import {TagModel} from '../../../../../models/api/tagModel';
import {EditTagDropdownContainer} from '../../../../common/dropdowns/tags/editTagDropdownContainer';
import {ConversationHeaderPill} from '../../conversation/header/conversationHeaderPill';
import {TagDropdownContainer} from '../../conversation/header/tag/tagDropdownContainer';
import {ActivityTagPillContent} from './activityTagPillContent';
import {ActivityTagPillTooltip} from './activityTagPillTooltip';

/*
 * Props.
 */

export interface ActivityTagPillProps {
  className?: string;
  panelId: ViewerPanelId;
  userId: number;
  teammate: TeammateModel;
  tag: TagModel;
  tagsPath: ReadonlyArray<TagModel>;
  hasPermission: (namespace: string, permission: PermissionsEnum) => boolean;
  teammates: ReadonlyArray<TeammateModel>;
  isTagAddedToConversation: boolean;
  onAddTag: () => void;
  onUntag: () => void;
  wasAddedByAi: boolean;
  aiMark?: ConversationAiMarkModel;
  conversation?: ConversationModel;
  maybeRenderInaccurateTagDialog?: (
    conversation: ConversationModel,
    aiMark: ConversationAiMarkModel,
    tagsPath: ReadonlyArray<TagModel>,
    onRequestClose: () => void,
  ) => ReactNode;
}

/*
 * Style.
 */

const StyledPillWrapperDiv = styled.div`
  position: relative;
  display: inline-block;
  margin-right: 2px;
`;

const StyledPopoverCoordinatorWrapperDiv = styled.div`
  margin: 0;
`;

/*
 * Component.
 */

export const ActivityTagPill: FC<ActivityTagPillProps> = (props) => {
  const {aiMark, conversation, maybeRenderInaccurateTagDialog} = props;
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isShowingInaccurateDialog, setIsShowingInaccurateDialog] = useState(false);

  return (
    <>
      <StyledPillWrapperDiv className={props.className}>
        <StyledPopoverCoordinatorWrapperDiv>
          <PopoverCoordinator
            interactionId="activity-tag-pill"
            renderButton={(isOpen) => renderPill(isOpen, props)}
            renderModal={(popoverProps) =>
              renderDropdown(popoverProps, props, isEditOpen, setIsEditOpen, () =>
                setIsShowingInaccurateDialog(true),
              )
            }
          />
        </StyledPopoverCoordinatorWrapperDiv>
      </StyledPillWrapperDiv>
      {isShowingInaccurateDialog &&
        conversation &&
        aiMark &&
        maybeRenderInaccurateTagDialog &&
        maybeRenderInaccurateTagDialog(conversation, aiMark, props.tagsPath, () => {
          setIsShowingInaccurateDialog(false);
        })}
    </>
  );
};

/*
 * Dropdown.
 */

function renderDropdown(
  popoverProps: PopoverTransferProps,
  props: ActivityTagPillProps,
  isEditOpen: boolean,
  setIsEditOpen: (val: boolean) => void,
  onShowInaccurateDialog: () => void,
) {
  if (isEditOpen) {
    return renderEditDialog(popoverProps, props, () => setIsEditOpen(false));
  }

  return renderListDropdown(popoverProps, props, () => setIsEditOpen(true), onShowInaccurateDialog);
}

/*
 * Pill.
 */

function renderPill(isOpen: boolean, props: ActivityTagPillProps) {
  const {teammate, tag, tagsPath} = props;

  // Special appearance for the Inbox tag.
  if (tag.isMainInboxTag) {
    return (
      <ConversationHeaderPill
        size={VisualSizesEnum.SMALL}
        hasHover={isOpen}
        isPrivate
        renderIcon={() => <Icon name="inboxFilledSmall" />}
      >
        <Trans id="activity-pill-tag-inboxtag">Inbox</Trans>
      </ConversationHeaderPill>
    );
  }

  const color = getColorHighlight(tag);
  const isPrivate = tag.namespace === teammate.namespace;

  // Normal case for any other tag.
  return (
    <ConversationHeaderPill
      size={VisualSizesEnum.SMALL}
      hasHover={isOpen}
      color={color}
      isPrivate={isPrivate}
      renderTooltip={() => !isOpen && renderPillTooltip(tagsPath)}
      renderIcon={() => (props.wasAddedByAi && <Icon name="sparklesAiFilledTiny" />) || null}
      shouldRenderIconWithInvertedBackground={props.wasAddedByAi}
    >
      <ActivityTagPillContent tagsPath={tagsPath} />
    </ConversationHeaderPill>
  );
}

function renderPillTooltip(tagsPath: ReadonlyArray<TagModel>) {
  return <ActivityTagPillTooltip tagsPath={tagsPath} />;
}

/*
 * Tag details dropdown.
 */

function renderListDropdown(
  popoverProps: PopoverTransferProps,
  props: ActivityTagPillProps,
  setIsEditOpen: () => void,
  onShowInaccurateDialog: () => void,
) {
  const {isTagAddedToConversation} = props;

  const onEditClick: EventHandler = (e) => {
    e.preventDefault(); // Keep the dropdown open
    setIsEditOpen(); // Switch to Edit Tag
  };

  return (
    <TagDropdownContainer
      {...popoverProps}
      panelId={props.panelId}
      teammate={props.teammate}
      tag={props.tag}
      isLastPrivateTag={false}
      onEditClick={onEditClick}
      isAddedToConversations={isTagAddedToConversation}
      onAddTagClick={props.onAddTag}
      onUntagClick={props.onUntag}
      wasAddedByAi={props.wasAddedByAi}
      onShowInaccurateDialog={onShowInaccurateDialog}
      selectedConversationIds={compact([props.conversation?.id])}
    />
  );
}

/*
 * Tag edit dialog.
 */

function renderEditDialog(
  popoverProps: PopoverTransferProps,
  props: ActivityTagPillProps,
  setIsEditOpen: () => void,
) {
  const onRequestClose = () => {
    popoverProps.onRequestClose(); // Close the dropdown
    setIsEditOpen(); // Reset back to List
  };

  return (
    <EditTagDropdownContainer
      {...props}
      {...popoverProps}
      onRequestClose={onRequestClose}
      onBackButtonClick={setIsEditOpen}
    />
  );
}
